import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import {
  FaFileInvoiceDollar,
  FaSearch,
  FaArrowLeft,
  FaArrowRight,
  FaCheckCircle,
  FaMoneyBillWave,
  FaCreditCard,
  FaReceipt,
} from "react-icons/fa";

export default function SaleOrderView() {
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [paymentFilter, setPaymentFilter] = useState("all");

  useEffect(() => {
    document.title = "Sales Invoices & Revenue — Admin";
    fetchInvoices();
  }, []);

  const fetchInvoices = () => {
    setLoading(true);
    axios.get(`/api/fetch-invoice`).then((res) => {
      if (res.data.status === 200) {
        setInvoices(res.data.data || []);
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  const getValidMoment = (date1, date2) => {
    if (date1 && typeof date1 === "string" && !date1.startsWith("0000") && moment(date1).isValid()) return moment(date1);
    if (date2 && typeof date2 === "string" && !date2.startsWith("0000") && moment(date2).isValid()) return moment(date2);
    return null;
  };

  // Filtered dataset: Keep active orders (including partially refunded ones that still have refundable items)
  const activeInvoices = invoices.filter(
    (data) => data.order_status !== "Refunded" && Number(data.status) !== 5
  );
  const filteredList = activeInvoices.filter((item) => {
    const term = searchTerm.toLowerCase();
    const matchesSearch =
      !searchTerm ||
      (item.tracking_no && item.tracking_no.toLowerCase().includes(term)) ||
      (item.firstname && item.firstname.toLowerCase().includes(term)) ||
      (item.lastname && item.lastname.toLowerCase().includes(term)) ||
      (item.email && item.email.toLowerCase().includes(term)) ||
      (item.phone && item.phone.toLowerCase().includes(term));

    const matchesPayment =
      paymentFilter === "all" ||
      (item.payment_mode && item.payment_mode.toLowerCase() === paymentFilter.toLowerCase());

    return matchesSearch && matchesPayment;
  });

  return (
    <div className="container-fluid px-0">
      {/* Page Header */}
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
        <div>
          <div className="d-flex align-items-center gap-2">
            <h3 className="fw-bold mb-0 text-dark" style={{ letterSpacing: "-0.5px" }}>
              Sales Orders & Revenue Invoices
            </h3>
            <span className="badge rounded-pill bg-success bg-opacity-10 text-success fw-semibold px-2.5 py-1">
              Active Invoices
            </span>
          </div>
          <p className="text-muted small mb-0 mt-1">
            Authoritative list of commercial sales invoices ready for return processing or financial audits.
          </p>
        </div>
        <div className="d-flex align-items-center gap-2">
          <Link
            to="/admin/refundView"
            className="btn btn-outline-secondary d-inline-flex align-items-center gap-2 fw-semibold px-3 py-2 rounded-3"
          >
            <FaArrowLeft /> Back to Refunds
          </Link>
          <Link
            to="/admin/orders"
            className="btn btn-primary d-inline-flex align-items-center gap-2 fw-semibold px-3 py-2 shadow-sm rounded-3"
          >
            <FaReceipt /> Order Management
          </Link>
        </div>
      </div>

      {/* KPI Stats Row */}
      <div className="row g-3 mb-4">
        <div className="col-md-4">
          <div className="admin-stat-card">
            <div className="admin-stat-header">
              <span className="admin-stat-label">Active Completed Sales</span>
              <div className="admin-stat-icon" style={{ background: "#dcfce7", color: "#16a34a" }}>
                <FaReceipt />
              </div>
            </div>
            <div className="admin-stat-value text-success">{activeInvoices.length}</div>
            <div className="admin-stat-footer text-muted">
              <FaCheckCircle className="text-success me-1" /> Recognized commercial transactions
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="admin-stat-card">
            <div className="admin-stat-header">
              <span className="admin-stat-label">Cash on Delivery</span>
              <div className="admin-stat-icon" style={{ background: "#fef3c7", color: "#d97706" }}>
                <FaMoneyBillWave />
              </div>
            </div>
            <div className="admin-stat-value text-dark">
              {activeInvoices.filter((i) => i.payment_mode === "COD").length}
            </div>
            <div className="admin-stat-footer text-muted">
              Settled via logistics courier partners
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="admin-stat-card">
            <div className="admin-stat-header">
              <span className="admin-stat-label">Bank transfer</span>
              <div className="admin-stat-icon" style={{ background: "#e0f2fe", color: "#0284c7" }}>
                <FaCreditCard />
              </div>
            </div>
            <div className="admin-stat-value text-primary">
              {activeInvoices.filter((i) => (i.payment_mode || "").toUpperCase() === "BT").length}
            </div>
            <div className="admin-stat-footer text-muted">
              Waiting for the customer transfer to clear
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Card */}
      <div className="admin-card">
        {/* Filter and Search Bar */}
        <div className="admin-card-header bg-white d-flex flex-wrap align-items-center justify-content-between gap-3 py-3">
          {/* Tabs */}
          <div className="d-flex align-items-center gap-2">
            <button
              onClick={() => setPaymentFilter("all")}
              className={`btn btn-sm rounded-pill px-3 fw-semibold ${
                paymentFilter === "all" ? "btn-dark" : "btn-light text-muted"
              }`}
            >
              All Payment Modes
            </button>
            <button
              onClick={() => setPaymentFilter("cod")}
              className={`btn btn-sm rounded-pill px-3 fw-semibold ${
                paymentFilter === "cod" ? "btn-dark" : "btn-light text-muted"
              }`}
            >
              COD Orders
            </button>
            <button
              onClick={() => setPaymentFilter("BT")}
              className={`btn btn-sm rounded-pill px-3 fw-semibold ${
                paymentFilter === "BT" ? "btn-dark" : "btn-light text-muted"
              }`}
            >
              Bank transfer
            </button>
          </div>

          {/* Search box */}
          <div className="position-relative" style={{ minWidth: "280px" }}>
            <FaSearch
              className="position-absolute text-muted"
              style={{ top: "11px", left: "12px", fontSize: "14px" }}
            />
            <input
              type="search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-control form-control-sm ps-5 rounded-pill border-light-subtle shadow-none"
              placeholder="Search by Tracking #, Name, Email..."
            />
          </div>
        </div>

        {/* Table / Content */}
        <div className="table-responsive">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status"></div>
              <p className="text-muted mt-2 small">Loading sales orders...</p>
            </div>
          ) : filteredList.length === 0 ? (
            <div className="text-center py-5">
              <div
                className="d-inline-flex align-items-center justify-content-center rounded-circle bg-light mb-3"
                style={{ width: "64px", height: "64px", color: "#94a3b8" }}
              >
                <FaFileInvoiceDollar size={24} />
              </div>
              <h6 className="fw-bold text-dark">No sales orders found</h6>
              <p className="text-muted small mb-0">
                {searchTerm
                  ? "No results matching your search criteria."
                  : "No sales records currently eligible for return processing."}
              </p>
            </div>
          ) : (
            <table className="admin-table mb-0">
              <thead>
                <tr>
                  <th style={{ width: "60px" }}>#</th>
                  <th>Order Reference</th>
                  <th>Customer Information</th>
                  <th>Payment Gateway</th>
                  <th>Completed Date</th>
                  <th>Fulfilled By</th>
                  <th className="text-end" style={{ width: "130px" }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredList.map((item, index) => {
                  const customerName = `${item.firstname || ""} ${item.lastname || ""}`.trim() || "Guest Customer";
                  const initial = customerName.charAt(0).toUpperCase();

                  return (
                    <tr key={index}>
                      <td className="text-muted fw-semibold">{index + 1}</td>
                      <td>
                        <div className="fw-bold text-dark font-monospace">
                          {item.tracking_no || `ORD-#${item.order_id}`}
                        </div>
                        <span className="text-muted small">Order ID: #{item.order_id}</span>
                      </td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <div
                            className="rounded-circle bg-success bg-opacity-10 text-success d-flex align-items-center justify-content-center fw-bold"
                            style={{ width: "32px", height: "32px", fontSize: "13px" }}
                          >
                            {initial}
                          </div>
                          <div>
                            <div className="fw-semibold text-dark">{customerName}</div>
                            <div className="text-muted small d-flex align-items-center gap-2">
                              {item.email && <span>{item.email}</span>}
                              {item.phone && <span>• {item.phone}</span>}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span
                          className={`admin-badge ${
                            item.payment_mode === "COD" ? "admin-badge-warning" : "admin-badge-info"
                          }`}
                        >
                          {item.payment_mode === "COD" ? <FaMoneyBillWave size={11} /> : <FaCreditCard size={11} />}
                          {(item.payment_mode || "").toUpperCase() === "BT" ? "Bank transfer" : (item.payment_mode || "COD")}
                        </span>
                      </td>
                      <td>
                        <div className="fw-medium text-dark">
                          {getValidMoment(item.order_complete, item.created_at)?.format("DD MMM YYYY") || "—"}
                        </div>
                        <div className="text-muted small">
                          {getValidMoment(item.order_complete, item.created_at)?.format("hh:mm A") || "—"}
                        </div>
                      </td>
                      <td>
                        <div className="fw-semibold small text-dark">{item.created_by || "System Admin"}</div>
                        {item.completed_email && (
                          <div className="text-muted small" style={{ fontSize: "11px" }}>
                            {item.completed_email}
                          </div>
                        )}
                      </td>
                      <td className="text-end">
                        <Link
                          to={`/admin/refund/sale-orders-details/${item.order_id}`}
                          className="btn btn-sm btn-primary fw-semibold d-inline-flex align-items-center gap-1 rounded-3 px-2.5 py-1 shadow-sm"
                        >
                          Manage Refund <FaArrowRight size={11} />
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
